import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Save, RotateCcw, Check, Copy, Trash2, Mic, Square, Upload, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ParagraphModule({
  order,
  sessionId,
  selectedModel,
  onConfirm,
  onTokensUsed,
  previousParagraphs = [],
  maxParagraphs = 20,
  customSettings,
  initialParagraph = null  // Add support for preloading saved paragraph
}) {
  const [prompt, setPrompt] = useState('');
  const [mainIdea, setMainIdea] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [paragraphId, setParagraphId] = useState(null);
  const [versions, setVersions] = useState([]);
  
  // Load initial paragraph if provided (from session restore)
  useEffect(() => {
    if (initialParagraph) {
      setContent(initialParagraph.content || '');
      setMainIdea(initialParagraph.main_idea || '');
      setParagraphId(initialParagraph.id);
      setConfirmed(true);
    }
  }, [initialParagraph]);
  
  // Audio states
  const [isRecording, setIsRecording] = useState(false);
  const [audioTranscript, setAudioTranscript] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Initialize Web Speech API
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setAudioTranscript(transcript);
        setPrompt(transcript); // Auto-fill prompt with transcript
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        toast.error(`Speech recognition error: ${event.error}`);
        setIsRecording(false);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const handleStartRecording = async () => {
    try {
      // Start audio recording
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioFile(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();

      // Start speech recognition
      if (recognitionRef.current) {
        recognitionRef.current.start();
        setIsRecording(true);
        toast.success('Recording started');
      } else {
        toast.error('Speech recognition not supported in this browser');
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to access microphone');
    }
  };

  const handleStopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    toast.success('Recording stopped');
  };

  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) { // 20MB limit
        toast.error('File too large. Maximum size is 20MB');
        return;
      }
      
      const validTypes = ['audio/mp3', 'audio/wav', 'audio/webm', 'audio/m4a', 'audio/mpeg'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|webm|m4a)$/i)) {
        toast.error('Invalid file type. Please upload MP3, WAV, or M4A');
        return;
      }
      
      setAudioFile(file);
      toast.success('Audio file uploaded');
      
      // Note: Actual transcription would require OpenAI Whisper API
      // For now, user can manually enter transcript
      toast.info('Please use the record feature for automatic transcription, or enter transcript manually');
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    // Validate required fields
    if (!sessionId || typeof sessionId !== 'string') {
      toast.error('Invalid session ID');
      console.error('Session ID issue:', sessionId);
      return;
    }

    if (!selectedModel || !selectedModel.provider || !selectedModel.name) {
      toast.error('Please select an AI model');
      console.error('Model selection issue:', selectedModel);
      return;
    }

    setLoading(true);
    try {
      // Build context from previous paragraphs
      const context = previousParagraphs.map(p => ({
        content: String(p.content || ''),
        order: String(p.order || 0)
      }));

      const payload = {
        session_id: String(sessionId),
        prompt: String(prompt),
        model_provider: String(selectedModel.provider),
        model_name: String(selectedModel.name),
        context: context.length > 0 ? context : null,
        main_idea: mainIdea ? String(mainIdea) : null
      };

      console.log('Sending generation request:', payload);

      const response = await axios.post(
        `${API}/admin/generation/generate`,
        payload,
        { withCredentials: true }
      );

      if (response.data.success) {
        setContent(response.data.content);
        
        // Save version
        setVersions(prev => [...prev, {
          content: response.data.content,
          timestamp: new Date().toISOString(),
          model: `${selectedModel.provider}/${selectedModel.name}`
        }]);
        
        // Track tokens
        if (response.data.tokens_used) {
          onTokensUsed(response.data.tokens_used);
        }
        
        toast.success('Content generated successfully!');
      }
    } catch (error) {
      console.error('Error generating content:', error);
      
      // Extract error message
      let errorMessage = 'Failed to generate content';
      if (error.response?.data?.detail) {
        // Handle both string and object error details
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        } else if (typeof error.response.data.detail === 'object') {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error('No content to save');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/generation/paragraphs`,
        {
          session_id: sessionId,
          content: content,
          order: order,
          main_idea: mainIdea || null,
          paragraph_id: paragraphId
        },
        { withCredentials: true }
      );

      if (!paragraphId) {
        setParagraphId(response.data.id);
      }
      
      toast.success('Paragraph saved!');
    } catch (error) {
      console.error('Error saving paragraph:', error);
      toast.error('Failed to save paragraph');
    }
  };

  const handleConfirm = () => {
    if (!content.trim()) {
      toast.error('Please generate or enter content first');
      return;
    }

    setConfirmed(true);
    onConfirm({
      id: paragraphId || `temp-${order}`,
      order: order,
      content: content,
      main_idea: mainIdea,
      versions: versions
    });
    
    toast.success(`Paragraph ${order} confirmed!`);
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset this paragraph? This will clear all content and versions.')) {
      setPrompt('');
      setMainIdea('');
      setContent('');
      setVersions([]);
      setConfirmed(false);
      setParagraphId(null);
      toast.info('Paragraph reset');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    toast.success('Content copied to clipboard');
  };

  const handleUndo = () => {
    if (versions.length > 1) {
      const previousVersion = versions[versions.length - 2];
      setContent(previousVersion.content);
      setVersions(prev => prev.slice(0, -1));
      toast.info('Undone to previous version');
    } else {
      toast.error('No previous version available');
    }
  };

  return (
    <div className={`admin-card ${confirmed ? 'border-4 border-green-500' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-2xl font-bold text-white">
          Paragraph {order}
          {confirmed && <span className="ml-2 text-green-400">✓ Confirmed</span>}
        </h3>
        <div className="text-sm text-gray-300">
          {order} of up to {maxParagraphs}
        </div>
      </div>

      {/* Main Idea Input */}
      <div className="mb-4">
        <label className="block text-white font-bold text-lg mb-2">
          Main Idea (Optional)
        </label>
        <input
          type="text"
          value={mainIdea}
          onChange={(e) => setMainIdea(e.target.value)}
          placeholder="What is the main point of this paragraph?"
          className="w-full px-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423]"
          disabled={confirmed}
        />
      </div>

      {/* Audio Recording Section */}
      {!confirmed && (
        <div className="mb-4">
          <label className="block text-white font-bold text-lg mb-2">
            Audio Input (Optional)
          </label>
          <div className="flex gap-2 mb-3">
            {!isRecording ? (
              <button
                onClick={handleStartRecording}
                className="retro-button flex items-center gap-2 bg-red-600 hover:bg-red-700"
                type="button"
              >
                <Mic className="w-4 h-4" />
                Start Recording
              </button>
            ) : (
              <button
                onClick={handleStopRecording}
                className="retro-button flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 animate-pulse"
                type="button"
              >
                <Square className="w-4 h-4" />
                Stop Recording
              </button>
            )}
            
            <label className="retro-button flex items-center gap-2 cursor-pointer">
              <Upload className="w-4 h-4" />
              Upload Audio
              <input
                type="file"
                accept="audio/*"
                onChange={handleAudioUpload}
                className="hidden"
              />
            </label>
            
            {audioFile && (
              <button
                onClick={() => {
                  setAudioFile(null);
                  setAudioTranscript('');
                  toast.info('Audio cleared');
                }}
                className="retro-button flex items-center gap-2 bg-gray-600"
                type="button"
              >
                <X className="w-4 h-4" />
                Clear Audio
              </button>
            )}
          </div>
          
          {isRecording && (
            <div className="p-3 bg-red-50 border-2 border-red-400 rounded mb-3">
              <p className="text-red-700 font-semibold flex items-center gap-2">
                <span className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></span>
                Recording in progress... Speak clearly into your microphone
              </p>
            </div>
          )}
          
          {audioFile && (
            <div className="p-3 bg-green-50 border-2 border-green-400 rounded mb-3">
              <p className="text-green-700 font-semibold">
                ✓ Audio file ready: {audioFile.name || 'Recorded audio'}
              </p>
            </div>
          )}
          
          {/* Live Transcript Display */}
          {audioTranscript && (
            <div className="mb-3">
              <label className="block text-white font-bold text-lg mb-2">
                Live Transcript (Editable)
              </label>
              <textarea
                value={audioTranscript}
                onChange={(e) => {
                  setAudioTranscript(e.target.value);
                  setPrompt(e.target.value);
                }}
                className="w-full px-4 py-3 border-2 border-[#8B6F47] rounded h-24 resize-y focus:outline-none focus:border-[#6B4423] text-gray-900 text-base"
                placeholder="Transcript will appear here as you speak..."
              />
            </div>
          )}
        </div>
      )}

      {/* Text Prompt Input */}
      <div className="mb-4">
        <label className="block text-white font-bold text-lg mb-2">
          Your Prompt
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe what you want this paragraph to cover..."
          className="w-full px-4 py-3 border-2 border-[#8B6F47] rounded h-32 resize-y focus:outline-none focus:border-[#6B4423] text-gray-900 text-base"
          disabled={confirmed}
        />
      </div>

      {/* Generate Button */}
      <div className="mb-4">
        <button
          onClick={handleGenerate}
          disabled={loading || confirmed}
          className="retro-button flex items-center gap-2 w-full justify-center"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-[#F4E4C1] border-t-transparent rounded-full animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Generate Content
            </>
          )}
        </button>
      </div>

      {/* Response Panel */}
      {content && (
        <div className="mb-4">
          <label className="block text-white font-bold text-lg mb-2">
            Generated Content
          </label>
          <div className="bg-white border-2 border-[#8B6F47] rounded p-4 min-h-[200px]">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-64 resize-y border-none focus:outline-none text-gray-900 text-base"
              disabled={confirmed}
            />
          </div>
          
          {/* Version History */}
          {versions.length > 1 && (
            <div className="mt-2 text-sm text-gray-600">
              Version {versions.length} of {versions.length}
              {!confirmed && (
                <button
                  onClick={handleUndo}
                  className="ml-4 text-[#6B4423] hover:underline"
                >
                  ← Undo
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        {content && !confirmed && (
          <>
            <button
              onClick={handleSave}
              className="retro-button flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
            
            <button
              onClick={handleCopy}
              className="retro-button flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copy
            </button>
            
            <button
              onClick={handleReset}
              className="retro-button flex items-center gap-2 bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4" />
              Reset
            </button>
          </>
        )}
        
        {content && !confirmed && (
          <button
            onClick={handleConfirm}
            className="retro-button flex items-center gap-2 bg-green-600 hover:bg-green-700 ml-auto"
          >
            <Check className="w-4 h-4" />
            Confirm Paragraph
          </button>
        )}
      </div>
    </div>
  );
}
